import CloseIcon from '@mui/icons-material/Close';
import React, { useCallback, useEffect, useState } from 'react';
import {
  bucketLabel,
  bucketOptionsFor,
  RECOGNITION_BUCKET_OPTIONS,
  type RecognitionBucketOption,
} from '../../constants/recognitionReview';

/** Level one: drop the primary bucket entirely. Free at both levels of the taxonomy. */
export const BUCKET_CHOOSER_CLEAR_KEY = 'X';
/** Level two: keep the top-level bucket without narrowing it to a sub-bucket. */
export const BUCKET_CHOOSER_NO_SUB_BUCKET_KEY = 'X';

/** 1 = top-level buckets, 2 = the sub-buckets of one top-level bucket. */
export type BucketChooserLevel = 1 | 2;

export type BucketChooserAction =
  | { kind: 'bucket'; path: string }
  | { kind: 'subBucket'; path: string }
  | { kind: 'noSubBucket' }
  | { kind: 'clear' };

/**
 * Resolve one keystroke against the bucket taxonomy for the chooser level it was typed at.
 *
 * Shortcut letters are unique within a level but repeat across levels, so the level - and, at level
 * two, the top-level bucket already chosen - decides what a letter means.
 *
 * @param level Chooser level the key was pressed at.
 * @param key The pressed key; case is ignored.
 * @param prefix The chosen top-level bucket path; required at level two, ignored at level one.
 * @returns The action the key selects, or `null` when the level has no option for it.
 */
export function bucketActionFromShortcutKey(
  level: BucketChooserLevel,
  key: string,
  prefix?: string | null
): BucketChooserAction | null {
  const normalizedKey = key.toUpperCase();
  if (level === 1) {
    if (normalizedKey === BUCKET_CHOOSER_CLEAR_KEY) return { kind: 'clear' };
    const option = RECOGNITION_BUCKET_OPTIONS.find(
      (candidate) => candidate.shortcutKey === normalizedKey
    );
    return option ? { kind: 'bucket', path: option.path } : null;
  }
  if (normalizedKey === BUCKET_CHOOSER_NO_SUB_BUCKET_KEY) return { kind: 'noSubBucket' };
  const option = bucketOptionsFor(prefix ?? '').find(
    (candidate) => candidate.shortcutKey === normalizedKey
  );
  return option ? { kind: 'subBucket', path: option.path } : null;
}

function OptionRow({
  option,
  isSelected,
  testId,
  onSelect,
}: {
  option: RecognitionBucketOption;
  isSelected: boolean;
  testId: string;
  onSelect: () => void;
}): JSX.Element {
  return (
    <button
      type="button"
      data-testid={testId}
      className={`rr-bucket-option${isSelected ? ' is-selected' : ''}`}
      onClick={onSelect}
    >
      <span>{option.label}</span>
      <span className="rr-bucket-key">{option.shortcutKey}</span>
    </button>
  );
}

/**
 * Two-level keyboard chooser for the Recognition issue bucket taxonomy.
 *
 * Level one lists the top-level buckets; picking one that has sub-buckets opens level two, where an
 * explicit "no sub-bucket" row records the bare top-level bucket. Escape backs out of level two
 * before it closes the chooser. Every other keystroke is swallowed while the chooser is open so the
 * page-level review shortcuts cannot fire behind it.
 */
export const BucketChooserOverlay: React.FC<{
  selectedPath?: string | null;
  onSelect: (path: string | null) => void;
  onClose: () => void;
}> = ({ selectedPath = null, onSelect, onClose }) => {
  const [prefix, setPrefix] = useState<string | null>(null);
  const level: BucketChooserLevel = prefix ? 2 : 1;

  const applyAction = useCallback(
    (action: BucketChooserAction) => {
      if (action.kind === 'clear') {
        onSelect(null);
        return;
      }
      if (action.kind === 'bucket') {
        // `other` carries no sub-buckets, so it resolves at level one.
        if (bucketOptionsFor(action.path).length === 0) {
          onSelect(action.path);
          return;
        }
        setPrefix(action.path);
        return;
      }
      if (action.kind === 'noSubBucket') {
        onSelect(prefix);
        return;
      }
      onSelect(action.path);
    },
    [onSelect, prefix]
  );

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target?.tagName === 'INPUT' ||
        target?.tagName === 'TEXTAREA' ||
        target?.isContentEditable
      ) {
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        if (prefix) setPrefix(null);
        else onClose();
        return;
      }
      if (event.key.length !== 1 || event.metaKey || event.ctrlKey || event.altKey) return;
      // Swallow every printable key while the chooser owns the keyboard, matched or not.
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      const action = bucketActionFromShortcutKey(level, event.key, prefix);
      if (action) applyAction(action);
    };
    document.addEventListener('keydown', handleKeyDown, { capture: true });
    return () => document.removeEventListener('keydown', handleKeyDown, { capture: true });
  }, [applyAction, level, onClose, prefix]);

  const subBuckets = prefix ? bucketOptionsFor(prefix) : [];

  return (
    <div className="rr-bucket-backdrop" onClick={onClose}>
      <div
        className="rr-bucket-chooser"
        role="dialog"
        aria-modal="true"
        aria-label="Recognition issue bucket"
        onClick={(event) => event.stopPropagation()}
      >
        <div className="rr-bucket-header">
          <div>
            <div className="rr-bucket-title">
              {prefix ? bucketLabel(prefix) : 'Recognition issue bucket'}
            </div>
            <div className="rr-bucket-copy">
              {prefix
                ? 'Narrow the primary bucket, or record it without a sub-bucket.'
                : 'What single change would most likely have made this sample confident correct?'}
            </div>
          </div>
          <button
            type="button"
            className="rr-bucket-close"
            aria-label="Close bucket chooser"
            onClick={onClose}
          >
            <CloseIcon style={{ fontSize: 14 }} />
          </button>
        </div>
        <div className="rr-bucket-options">
          {prefix ? (
            <>
              {subBuckets.map((option) => (
                <OptionRow
                  key={option.path}
                  option={option}
                  isSelected={selectedPath === option.path}
                  testId={`bucket-chooser-option-${option.path}`}
                  onSelect={() => applyAction({ kind: 'subBucket', path: option.path })}
                />
              ))}
              <div className="rr-bucket-divider" />
              <button
                type="button"
                data-testid="bucket-chooser-no-sub-bucket"
                className="rr-bucket-option"
                onClick={() => applyAction({ kind: 'noSubBucket' })}
              >
                <span>No sub-bucket</span>
                <span className="rr-bucket-key">{BUCKET_CHOOSER_NO_SUB_BUCKET_KEY}</span>
              </button>
            </>
          ) : (
            <>
              {RECOGNITION_BUCKET_OPTIONS.map((option) => (
                <OptionRow
                  key={option.path}
                  option={option}
                  isSelected={selectedPath?.startsWith(option.path) ?? false}
                  testId={`bucket-chooser-option-${option.path}`}
                  onSelect={() => applyAction({ kind: 'bucket', path: option.path })}
                />
              ))}
              <div className="rr-bucket-divider" />
              <button
                type="button"
                data-testid="bucket-chooser-clear"
                className="rr-bucket-option is-clear"
                onClick={() => applyAction({ kind: 'clear' })}
              >
                <span>Clear primary bucket</span>
                <span className="rr-bucket-key">{BUCKET_CHOOSER_CLEAR_KEY}</span>
              </button>
            </>
          )}
        </div>
        <div className="rr-bucket-hint">
          {prefix ? 'Esc goes back to the bucket list.' : 'Esc closes this chooser.'}
        </div>
      </div>
    </div>
  );
};

export default BucketChooserOverlay;
